import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Grid,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Divider,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardActionArea,
  Chip,
  CardContent,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  MyLocation as MyLocationIcon,
  LocationOn as LocationOnIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Msg91OtpWidget, { isMsg91WidgetConfigured } from '../../components/Msg91OtpWidget';
import {
  cityFromNominatimAddress,
  gpsMismatchWithPincode,
  resolveLocationFromPincode,
} from '../../utils/locationHelpers';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../services/api';
import { openRazorpayCheckout } from '../../services/paymentsApi';
import BusinessCategoriesPicker from '../../components/Franchisee/BusinessCategoriesPicker';
import useBusinessCategoryOptions from '../../hooks/useBusinessCategoryOptions';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const normalizePhoneInput = (value) => value.replace(/\D/g, '').slice(0, 10);

const handlePhoneKeyDown = (event, currentValue) => {
  const allowedControlKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'ArrowLeft',
    'ArrowRight',
    'Home',
    'End',
  ];

  if (allowedControlKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
    return;
  }

  if (/^\d$/.test(event.key) && normalizePhoneInput(currentValue).length >= 10) {
    event.preventDefault();
  }
};

const phoneValidationSchema = yup
  .string()
  .required('Phone number is required')
  .test('phone-length', 'Phone number is required', function (value) {
    const digits = normalizePhoneInput(value || '');

    if (!digits) {
      return this.createError({ message: 'Phone number is required' });
    }

    if (digits.length < 10) {
      const remainingDigits = 10 - digits.length;
      return this.createError({
        message: `${digits.length} digit${digits.length === 1 ? '' : 's'} entered, ${remainingDigits} more needed`,
      });
    }

    if (digits.length > 10) {
      return this.createError({ message: 'Phone number cannot exceed 10 digits' });
    }

    return true;
  });

const validationSchema = yup.object({
  business_name: yup
    .string()
    .required('Business/Service name is required')
    .min(2, 'Business name is too short'),
  business_categories: yup
    .array()
    .of(yup.string().min(1, 'Invalid category'))
    .min(1, 'Add at least one business category')
    .max(8, 'Maximum 8 categories allowed'),
  contact_person_name: yup
    .string()
    .required('Contact person name is required')
    .min(2, 'Contact person name is too short'),
  phone: phoneValidationSchema,
  email: yup.string().email('Invalid email address').optional(),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
  confirmPassword: yup
    .string()
    .required('Confirm password is required')
    .oneOf([yup.ref('password')], 'Passwords must match'),
  login_pin: yup
    .string()
    .optional()
    .test('pin-digits', 'Login PIN must be 4 to 9 digits', (value) => {
      if (!value) return true;
      return /^\d{4,9}$/.test(String(value));
    }),
  confirm_login_pin: yup.string().when('login_pin', {
    is: (v) => Boolean(v && String(v).length > 0),
    then: (schema) =>
      schema
        .required('Confirm your login PIN')
        .oneOf([yup.ref('login_pin')], 'PINs must match'),
    otherwise: (schema) => schema.optional(),
  }),
  otp: yup.string().optional(),
  requested_plan: yup
    .string()
    .required('Plan type is required'),
  address: yup
    .string()
    .required('Business address is required')
    .min(5, 'Enter a complete street address'),
  city: yup.string().required('City is required'),
  state: yup.string().optional(),
  pincode: yup
    .string()
    .required('Pincode is required')
    .matches(/^\d{6}$/, 'Pincode must be exactly 6 digits'),
  location_latitude: yup
    .string()
    .required('GPS location is required — tap “Use my location”')
    .test('valid-lat', 'Invalid latitude', (value) => {
      const n = parseFloat(value);
      return !Number.isNaN(n) && n >= -90 && n <= 90;
    }),
  location_longitude: yup
    .string()
    .required('GPS location is required — tap “Use my location”')
    .test('valid-lng', 'Invalid longitude', (value) => {
      const n = parseFloat(value);
      return !Number.isNaN(n) && n >= -180 && n <= 180;
    }),
});

const FALLBACK_MEMBERSHIP_PLANS = [
  {
    planKey: 'connect',
    name: 'Loksy Connect',
    purpose: 'Business Discovery',
    tagline: 'Get Discovered by Local Customers',
    badgeLabel: 'Free',
    monthlyPricePaise: 0,
    isFree: true,
    isHighlighted: false,
  },
  {
    planKey: 'elite',
    name: 'Loksy Elite',
    purpose: 'Business Management',
    tagline: 'Manage Your Business Professionally',
    badgeLabel: 'Popular',
    monthlyPricePaise: 49900,
    isFree: false,
    isHighlighted: true,
  },
  {
    planKey: 'prime',
    name: 'Loksy Prime',
    purpose: 'Business Growth',
    tagline: 'Grow Faster with Premium Visibility',
    badgeLabel: 'Best value',
    monthlyPricePaise: 149900,
    isFree: false,
    isHighlighted: false,
  },
];

const FranchiseeAdminRegister = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return (params.get('nav_ref') || params.get('navRef') || '').trim();
  }, [location.search]);
  const { completeLogin } = useAuth();
  /** OTP via MSG91 browser widget only (same as Business Mitra / Forgot Password). */
  const msg91Ready = isMsg91WidgetConfigured();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [msg91AccessToken, setMsg91AccessToken] = useState('');
  const [msg91RequestUserId, setMsg91RequestUserId] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [existingLoksyUser, setExistingLoksyUser] = useState(false);

  const googleEmail = useMemo(
    () => String(location.state?.googleAuth?.email || '').trim().toLowerCase(),
    [location.state]
  );
  const googleName = useMemo(
    () => String(location.state?.googleAuth?.full_name || '').trim(),
    [location.state]
  );

  const [regStep, setRegStep] = useState(0);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [membershipTrialConsent, setMembershipTrialConsent] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [pincodeLocationLoading, setPincodeLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [membershipPlans, setMembershipPlans] = useState(FALLBACK_MEMBERSHIP_PLANS);

  const formik = useFormik({
    initialValues: {
      business_name: '',
      business_categories: [],
      contact_person_name: googleName,
      phone: '',
      email: googleEmail,
      password: '',
      confirmPassword: '',
      login_pin: '',
      confirm_login_pin: '',
      otp: '',
      requested_plan: 'postpaid_trial',
      membership_plan_key: 'connect',
      address: '',
      city: '',
      state: '',
      pincode: '',
      location_latitude: '',
      location_longitude: '',
    },
    validationSchema: validationSchema,
    onSubmit: async () => {},
  });

  const loadMembershipCatalog = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/membership/public/plans`);
      const data = res.data?.data;
      if (res.data?.success && data) {
        if (Array.isArray(data.plans) && data.plans.length > 0) {
          setMembershipPlans(data.plans);
        }
      }
    } catch {
      /* keep fallback */
    }
  };

  const selectedMembership = useMemo(() => {
    return (
      membershipPlans.find((p) => p.planKey === formik.values.membership_plan_key) ||
      membershipPlans[0] ||
      FALLBACK_MEMBERSHIP_PLANS[0]
    );
  }, [membershipPlans, formik.values.membership_plan_key]);

  const step1Cta = 'Continue';
  const step2Cta = 'Create account';

  useEffect(() => {
    loadMembershipCatalog();
  }, []);


  useEffect(() => {
    const phone = normalizePhoneInput(formik.values.phone || '');
    if (phone.length !== 10) {
      setExistingLoksyUser(false);
      return undefined;
    }
    let cancelled = false;
    axios.post(`${API_BASE_URL}/franchisee/public/check-phone`, { phone })
      .then((response) => {
        if (!cancelled) setExistingLoksyUser(response.data?.data?.loksy_user_exists === true);
      })
      .catch(() => { if (!cancelled) setExistingLoksyUser(false); });
    return () => { cancelled = true; };
  }, [formik.values.phone]);
  const applyResolvedPincodeLocation = async (resolved) => {
    await formik.setFieldValue('location_latitude', String(resolved.lat));
    await formik.setFieldValue('location_longitude', String(resolved.lng));
    formik.setFieldTouched('location_latitude', true, false);
    formik.setFieldTouched('location_longitude', true, false);
    await formik.setFieldValue('pincode', resolved.pincode);
    formik.setFieldTouched('pincode', true, false);
    if (resolved.city) {
      await formik.setFieldValue('city', resolved.city);
      formik.setFieldTouched('city', true, false);
    }
    if (resolved.state) {
      await formik.setFieldValue('state', resolved.state);
    }
  };

  const handleSetLocationFromPincode = async () => {
    setLocationError('');
    setSuccess('');
    const pin = String(formik.values.pincode || '').replace(/\D/g, '');
    if (pin.length !== 6) {
      setLocationError('Enter your 6-digit pincode first (e.g. 534460 for Chintalapudi).');
      return;
    }

    setPincodeLocationLoading(true);
    try {
      const resolved = await resolveLocationFromPincode(pin);
      if (!resolved.ok) {
        setLocationError(resolved.message || 'Could not resolve pincode.');
        return;
      }
      await applyResolvedPincodeLocation(resolved);
      setSuccess(
        `Location set from pincode ${resolved.pincode} (${resolved.city || 'area'}). Add your street address below.`
      );
    } catch (err) {
      setLocationError(err?.message || 'Pincode lookup failed. Try again.');
    } finally {
      setPincodeLocationLoading(false);
    }
  };

  const handleCaptureBusinessLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Location is not supported in this browser.');
      return;
    }

    setLocationLoading(true);
    setLocationError('');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        await formik.setFieldValue('location_latitude', String(lat));
        await formik.setFieldValue('location_longitude', String(lng));
        formik.setFieldTouched('location_latitude', true, false);
        formik.setFieldTouched('location_longitude', true, false);

        const pinEntered = String(formik.values.pincode || '').replace(/\D/g, '');
        const mismatch =
          pinEntered.length === 6
            ? gpsMismatchWithPincode(lat, lng, pinEntered)
            : null;

        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
            {
              headers: {
                Accept: 'application/json',
                'Accept-Language': 'en',
              },
            }
          );
          if (response.ok && !mismatch) {
            const data = await response.json();
            const parts = data?.address || {};
            const line = [
              parts.house_number,
              parts.road || parts.neighbourhood,
              parts.suburb,
            ]
              .filter(Boolean)
              .join(', ');
            const cityName = cityFromNominatimAddress(parts);
            const postcode = String(parts.postcode || '').replace(/\D/g, '').slice(0, 6);
            if (line) {
              await formik.setFieldValue('address', line);
              formik.setFieldTouched('address', true, false);
            }
            if (cityName) {
              await formik.setFieldValue('city', cityName);
              formik.setFieldTouched('city', true, false);
            }
            if (parts.state) {
              await formik.setFieldValue('state', parts.state);
            }
            if (postcode.length === 6 && !pinEntered) {
              await formik.setFieldValue('pincode', postcode);
              formik.setFieldTouched('pincode', true, false);
            }
          }
        } catch {
          // GPS still saved; user can type address manually.
        }

        setLocationLoading(false);
        if (mismatch) {
          setLocationError(
            `Browser GPS is about ${mismatch.km} km from pincode ${mismatch.pincode} (${mismatch.expectedCity}). On laptop/PC GPS often shows Hyderabad wrongly. Use “Set from pincode” instead.`
          );
          setSuccess('GPS coordinates saved, but address was not auto-filled. Prefer “Set from pincode”.');
        } else {
          setSuccess('Location captured. Verify address, city, and pincode.');
        }
      },
      (geoError) => {
        setLocationLoading(false);
        setLocationError(
          geoError?.message ||
            'Could not access location. Allow GPS permission, or use “Set from pincode”.'
        );
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  };

  const handleStep0Next = async () => {
    setError('');
    setSuccess('');
    const touched = {};
    Object.keys(formik.values).forEach((k) => {
      if (k === 'otp') return;
      touched[k] = true;
    });
    formik.setTouched(touched);
    const errs = await formik.validateForm();
    if (Object.keys(errs).length) {
      setError('Please fix the highlighted fields (including business location & pincode).');
      return;
    }
    if (!msg91Ready) {
      setError(
        'MSG91 OTP is not configured. Set REACT_APP_MSG91_OTP_WIDGET_ID and REACT_APP_MSG91_OTP_WIDGET_TOKEN_AUTH in admin-panel/.env and restart npm start.'
      );
      return;
    }
    if (!phoneVerified || !msg91AccessToken) {
      setError('Verify mobile: tap Send OTP in the widget → enter SMS code → tap Verify OTP.');
      return;
    }
    setRegStep(1);
  };

  const submitRegistration = async () => {
    if (!termsAccepted) {
      setError('Please read and accept the registration terms before creating your account.');
      return;
    }
    if (!membershipTrialConsent) {
      setError('Please confirm that you understand the trial or Free listing before creating your account.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const values = formik.values;
      const primaryKey = values.business_categories[0] || '';
      const primaryOption = businessCategoryOptions.find(
        (opt) => String(opt.value || opt.category_key || '') === primaryKey,
      );
      const payload = {
        phone: values.phone,
        email: values.email.trim() || undefined,
        business_name: values.business_name,
        business_type: primaryKey,
        business_categories: values.business_categories,
        main_category_id:
          primaryOption?.mainCategoryId ||
          primaryOption?.main_category_id ||
          undefined,
        sub_category_id:
          primaryOption?.subCategoryId ||
          primaryOption?.sub_category_id ||
          undefined,
        sub_category_name: primaryOption?.label || undefined,
        contact_person_name: values.contact_person_name,
        // Not shown in UI — sent so older cloud APIs that still require these succeed.
        date_of_birth: '1900-01-01',
        password: values.password,
        ...(String(values.login_pin || '').replace(/\D/g, '').length >= 4
          ? { login_pin: String(values.login_pin).replace(/\D/g, '') }
          : {}),
        hint_question: 'Not collected',
        hint_answer: 'not-collected',
        requested_plan: 'postpaid_trial',
        membership_plan_key: values.membership_plan_key || 'connect',
        address: values.address.trim(),
        city: values.city.trim(),
        state: values.state.trim() || undefined,
        pincode: values.pincode.replace(/\s/g, ''),
        location_latitude: parseFloat(values.location_latitude),
        location_longitude: parseFloat(values.location_longitude),
        registration_payment_intent: 'pay_later',
        terms_accepted: true,
        membership_trial_consent: true,
        terms_version: 'mitra-registration-v1-2026-07',
        ...(navRef ? { nav_ref: navRef } : {}),
      };
      payload.msg91_access_token = msg91AccessToken;
      if (msg91RequestUserId) {
        payload.msg91_request_user_id = msg91RequestUserId;
      }
      const response = await axios.post(
        `${API_BASE_URL}/franchisee/admin/register`,
        payload
      );

      if (!response.data.success) {
        setError(response.data.message || 'Registration failed');
        return;
      }

      const { token, user, data } = response.data;
      const registration = data?.registration;

      if (token && user) {
        completeLogin(token, user, 'admin');
      }

      const rz = registration?.razorpay;
      if (rz?.order && rz?.keyId) {
        try {
          const rzResponse = await openRazorpayCheckout({
            order: rz.order,
            keyId: rz.keyId,
            customer: {
              name: values.contact_person_name,
              phone: values.phone,
            },
            description: 'Franchisee registration fee',
          });
          await apiClient.post('/franchisee/admin/registration/complete-razorpay', {
            razorpay_order_id: rzResponse.razorpay_order_id,
            razorpay_payment_id: rzResponse.razorpay_payment_id,
            razorpay_signature: rzResponse.razorpay_signature,
          });
          setSuccess(
            'Payment successful. Your business is published as Not verified and Mitra access is ready.',
          );
          setTimeout(() => navigate('/locsy/dashboard'), 2000);
        } catch (e) {
          if (e?.message === 'Payment cancelled') {
            setSuccess(
              response.data.message ||
                'Account created. You can complete payment from your dashboard when ready.'
            );
            setTimeout(() => navigate('/locsy/dashboard'), 2000);
          } else {
            setError(
              e.response?.data?.message ||
                e?.message ||
                'Could not complete payment. You can pay later from your dashboard.'
            );
            setTimeout(() => navigate('/locsy/dashboard'), 2500);
          }
        }
      } else {
        setSuccess(
          response.data.message ||
            'Registration successful. Your business is published as Not verified and Mitra dashboard access is ready.',
        );
        setTimeout(() => navigate('/locsy/dashboard'), 1500);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const { options: businessCategoryOptions, loading: categoriesLoading, error: categoriesError, loadedFromApi: categoriesFromApi } =
    useBusinessCategoryOptions();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="md" sx={{ px: { xs: 1.5, sm: 2 } }}>
        <Paper
          elevation={24}
          sx={{
            p: { xs: 2.5, sm: 4 },
            borderRadius: { xs: 2, sm: 3 },
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}
        >
          <Box
            sx={{
              textAlign: 'center',
              mb: 3,
              py: 2,
              px: 2,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
            }}
          >
            <Typography variant="h4" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              LOCSY Business Dashboard
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.95, fontWeight: 500 }}>
              Register your business — quick, mobile-friendly setup
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Stepper activeStep={regStep} sx={{ mb: 3 }} alternativeLabel>
            <Step>
              <StepLabel>Info & verify</StepLabel>
            </Step>
            <Step>
              <StepLabel>Loksy plan</StepLabel>
            </Step>
            <Step>
              <StepLabel>Complete</StepLabel>
            </Step>
          </Stepper>

          {regStep === 0 ? (
          <Box component="form" onSubmit={(e) => { e.preventDefault(); handleStep0Next(); }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Business / Service Name"
                  name="business_name"
                  value={formik.values.business_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.business_name && Boolean(formik.errors.business_name)}
                  helperText={formik.touched.business_name && formik.errors.business_name}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Pincode"
                  name="pincode"
                  required
                  value={formik.values.pincode}
                  onChange={(event) => {
                    const digits = event.target.value.replace(/\D/g, '').slice(0, 6);
                    formik.setFieldValue('pincode', digits);
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.pincode && Boolean(formik.errors.pincode)}
                  helperText={
                    formik.touched.pincode && formik.errors.pincode
                      ? formik.errors.pincode
                      : 'e.g. 534460 for Chintalapudi — use this first on laptop/PC'
                  }
                  inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  required
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={formik.touched.city && formik.errors.city}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Business location (required)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  On desktop, browser GPS often shows Hyderabad by mistake. Enter pincode, tap{' '}
                  <strong>Set from pincode</strong>, then type your street address.
                </Typography>
                {locationError && (
                  <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setLocationError('')}>
                    {locationError}
                  </Alert>
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    startIcon={
                      pincodeLocationLoading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <LocationOnIcon />
                      )
                    }
                    onClick={handleSetLocationFromPincode}
                    disabled={pincodeLocationLoading || locationLoading}
                  >
                    {pincodeLocationLoading ? 'Setting…' : 'Set from pincode'}
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    startIcon={
                      locationLoading ? <CircularProgress size={18} /> : <MyLocationIcon />
                    }
                    onClick={handleCaptureBusinessLocation}
                    disabled={locationLoading || pincodeLocationLoading}
                  >
                    {locationLoading ? 'Getting GPS…' : 'Use my location (phone)'}
                  </Button>
                </Stack>
                {formik.values.location_latitude && formik.values.location_longitude ? (
                  <Typography variant="caption" color="success.main" display="block" sx={{ mb: 1 }}>
                    GPS: {Number(formik.values.location_latitude).toFixed(5)},{' '}
                    {Number(formik.values.location_longitude).toFixed(5)}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="error" display="block" sx={{ mb: 1 }}>
                    Map location not set — enter pincode and tap “Set from pincode”
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Business address"
                  name="address"
                  required
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.address && Boolean(formik.errors.address)}
                  helperText={formik.touched.address && formik.errors.address}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formik.values.phone}
                  onChange={(event) => {
                    formik.setFieldValue(
                      'phone',
                      normalizePhoneInput(event.target.value)
                    );
                    setMsg91AccessToken('');
                    setMsg91RequestUserId('');
                    setPhoneVerified(false);
                  }}
                  onKeyDown={(event) => handlePhoneKeyDown(event, formik.values.phone)}
                  onBlur={formik.handleBlur}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={
                    formik.touched.phone && formik.errors.phone
                      ? formik.errors.phone
                      : (() => {
                          const digits = normalizePhoneInput(formik.values.phone || '');
                          const count = digits.length;
                          if (count === 0) return '0/10 · Enter your 10-digit mobile number';
                          if (count < 10) {
                            const remaining = 10 - count;
                            return `${count}/10 · ${remaining} more digit${remaining === 1 ? '' : 's'} needed`;
                          }
                          return '10/10 · Ready — continue phone verification';
                        })()
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon />
                        <Typography
                          component="span"
                          sx={{ ml: 0.5, fontWeight: 600, color: 'text.secondary' }}
                        >
                          +91
                        </Typography>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color:
                              normalizePhoneInput(formik.values.phone || '').length === 10
                                ? 'success.main'
                                : 'text.secondary',
                          }}
                        >
                          {normalizePhoneInput(formik.values.phone || '').length}/10
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    maxLength: 10,
                    inputMode: 'numeric',
                    type: 'tel',
                    pattern: '[0-9]*',
                  }}
                />
              </Grid>

              {existingLoksyUser && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    This mobile number is already registered with Loksy. Continue with Vyapar and set a new Vyapar PIN; your Citizen / Smart Bill account stays unchanged.
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <Stack spacing={1.5} sx={{ width: '100%', alignItems: 'center' }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      Phone verification
                    </Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Stack>
                  <Stack spacing={1.5} sx={{ width: '100%', maxWidth: 520, mx: 'auto' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
                      OTP is sent by MSG91 directly to your phone (not via our backend SMS).
                      Tap <strong>Send OTP</strong> → enter the SMS code → <strong>Verify OTP</strong>.
                      If you don&apos;t get the SMS, use <strong>Resend OTP</strong> after the timer ends.
                    </Typography>
                    <Msg91OtpWidget
                      phoneDigits10={formik.values.phone}
                      disabled={loading || normalizePhoneInput(formik.values.phone).length !== 10}
                      onVerified={(token, meta) => {
                        setMsg91AccessToken(token);
                        const sessionId =
                          meta &&
                          String(meta.requestUserId || meta.reqId || '').trim();
                        setMsg91RequestUserId(sessionId || '');
                        setPhoneVerified(true);
                        setError('');
                        setSuccess('Phone verified. You can continue to the next step.');
                      }}
                      onError={(msg) => {
                        setMsg91AccessToken('');
                        setMsg91RequestUserId('');
                        setPhoneVerified(false);
                        setError(msg || 'MSG91 verification failed');
                      }}
                    />
                    {phoneVerified && (
                      <Alert severity="success">
                        Mobile verified: +91{normalizePhoneInput(formik.values.phone)}
                      </Alert>
                    )}
                  </Stack>
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <BusinessCategoriesPicker
                  categories={formik.values.business_categories}
                  categoryOptions={businessCategoryOptions}
                  onChange={(next) => {
                    formik.setFieldValue('business_categories', next);
                    formik.setFieldTouched('business_categories', true, false);
                  }}
                  touched={formik.touched.business_categories}
                  error={
                    typeof formik.errors.business_categories === 'string'
                      ? formik.errors.business_categories
                      : Array.isArray(formik.errors.business_categories)
                        ? formik.errors.business_categories.find(Boolean)
                        : undefined
                  }
                />
                {categoriesLoading && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Loading latest shop & service list…
                  </Typography>
                )}
                {!categoriesLoading && !categoriesFromApi && categoriesError && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {categoriesError}. Restart backend if this persists — category list loads from{' '}
                    <code>/api/franchisee/public/business-categories</code>.
                  </Alert>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Person Name"
                  name="contact_person_name"
                  value={formik.values.contact_person_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.contact_person_name && Boolean(formik.errors.contact_person_name)}
                  helperText={formik.touched.contact_person_name && formik.errors.contact_person_name}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address (Optional)"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={Boolean(googleEmail)}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={
                    formik.touched.email && formik.errors.email
                      ? formik.errors.email
                      : googleEmail
                        ? 'Verified via Google login'
                        : ''
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  autoComplete="new-password"
                  error={
                    formik.touched.password && Boolean(formik.errors.password)
                  }
                  helperText={formik.touched.password && formik.errors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  autoComplete="new-password"
                  error={
                    formik.touched.confirmPassword &&
                    Boolean(formik.errors.confirmPassword)
                  }
                  helperText={
                    formik.touched.confirmPassword &&
                    formik.errors.confirmPassword
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Optional login PIN
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  Set a 4–9 digit PIN to sign in with phone + PIN (password still works).
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Login PIN (optional)"
                  name="login_pin"
                  type="password"
                  value={formik.values.login_pin}
                  onChange={(event) => {
                    const digits = String(event.target.value || '')
                      .replace(/\D/g, '')
                      .slice(0, 9);
                    formik.setFieldValue('login_pin', digits);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.login_pin && Boolean(formik.errors.login_pin)
                  }
                  helperText={
                    (formik.touched.login_pin && formik.errors.login_pin) ||
                    '4–9 digits'
                  }
                  inputProps={{ maxLength: 9, inputMode: 'numeric' }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirm PIN"
                  name="confirm_login_pin"
                  type="password"
                  value={formik.values.confirm_login_pin}
                  onChange={(event) => {
                    const digits = String(event.target.value || '')
                      .replace(/\D/g, '')
                      .slice(0, 9);
                    formik.setFieldValue('confirm_login_pin', digits);
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.confirm_login_pin &&
                    Boolean(formik.errors.confirm_login_pin)
                  }
                  helperText={
                    formik.touched.confirm_login_pin &&
                    formik.errors.confirm_login_pin
                  }
                  inputProps={{ maxLength: 9, inputMode: 'numeric' }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                  Loksy shop plan
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Choose the plan for your shop. It controls the features available in Mitra.
                </Typography>
                <Grid container spacing={1.5}>
                  {membershipPlans.map((plan) => {
                    const key = plan.planKey || plan.plan_key;
                    const selected = formik.values.membership_plan_key === key;
                    const monthly = Number(plan.monthlyPricePaise || plan.monthly_price_paise || 0);
                    const priceLabel = plan.isFree || monthly <= 0 ? 'Free' : `₹${Math.round(monthly / 100)}/mo`;
                    return (
                      <Grid item xs={12} sm={4} key={key}>
                        <Card
                          variant="outlined"
                          sx={{
                            borderColor: selected ? '#ea580c' : 'divider',
                            borderWidth: selected ? 2 : 1,
                            bgcolor: selected ? 'rgba(234, 88, 12, 0.06)' : 'background.paper',
                            height: '100%',
                          }}
                        >
                          <CardActionArea
                            onClick={() => formik.setFieldValue('membership_plan_key', key)}
                            sx={{ height: '100%', alignItems: 'stretch' }}
                          >
                            <CardContent>
                              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                                <Typography variant="subtitle1" fontWeight={800}>{plan.name}</Typography>
                                <Typography variant="body2" fontWeight={800} color={monthly <= 0 ? 'success.main' : 'warning.main'}>
                                  {priceLabel}
                                </Typography>
                              </Stack>
                              {(plan.badgeLabel || plan.badge_label) ? (
                                <Chip size="small" label={plan.badgeLabel || plan.badge_label} sx={{ mb: 1 }} color={plan.isHighlighted || plan.is_highlighted ? 'warning' : 'default'} />
                              ) : null}
                              <Typography variant="body2" color="text.secondary">{plan.tagline}</Typography>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>

            </Grid>

            <Alert severity="info" sx={{ mt: 2 }}>
              After registration your shop is published with a Not verified badge and Mitra opens
              immediately. Your Loksy plan controls what citizens see; upgrade anytime from Mitra.
            </Alert>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : step1Cta}
            </Button>

            <Box sx={{ textAlign: 'center', pb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Already registered?{' '}
                <Link
                  to="/locsydashboard/login"
                  style={{ textDecoration: 'none', color: '#667eea', fontWeight: 600 }}
                >
                  Login here
                </Link>
              </Typography>
            </Box>
          </Box>
          ) : (
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Complete registration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your selected Loksy plan ({selectedMembership?.name || 'Connect'}) controls the features available in Mitra.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Registration activates automatically. Your shop is published with a Not verified badge.
            </Alert>
`r`n            <Alert severity="warning" sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>
                Important terms
              </Typography>
              <Typography variant="body2" component="div" sx={{ lineHeight: 1.55 }}>
                • Your shop is published immediately with a Not verified badge; this is not a LOCSY endorsement.<br />
                • An authorized admin may verify, moderate, suspend, or unlist it without prior notice when
                required by platform policy, safety, legal requests, or applicable law.<br />
                • You confirm all business details, media, offers, and contact information are accurate,
                lawful, and authorized for publication.<br />
                • Misleading, obscene, infringing, fraudulent, prohibited, or unlawful content is not allowed.
                You remain responsible for your content and lawful customer-data usage.<br />
                • Account and listing data may be processed for authentication, directory publication,
                security, payments, support, and service notices under the Privacy Policy.<br />
                • Mitra tools are free for 1 month. A 5-day countdown appears before restriction. Payment is
                required to continue without interruption or resume services; Citizen listing is separate.
              </Typography>
            </Alert>

            <FormControl error={!termsAccepted} sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => {
                      setTermsAccepted(e.target.checked);
                      if (e.target.checked) setError('');
                    }}
                  />
                }
                label={
                  <Typography variant="body2">
                    I have read and agree to these registration terms, the Terms of Service, Privacy
                    Policy, content rules, post-trial payment requirement, and admin
                    moderation/unlisting rights.
                  </Typography>
                }
              />
              {!termsAccepted && (
                <FormHelperText>Required to create the account</FormHelperText>
              )}
            </FormControl>

            <FormControl error={!membershipTrialConsent} sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={membershipTrialConsent}
                    onChange={(e) => {
                      setMembershipTrialConsent(e.target.checked);
                      if (e.target.checked) setError('');
                    }}
                  />
                }
                label={
                  <Typography variant="body2">
                    I understand the trial / Free listing and what happens when it ends. No money is
                    taken now; a paid membership requires a separate purchase.
                  </Typography>
                }
              />
              {!membershipTrialConsent && (
                <FormHelperText>Required to create the account</FormHelperText>
              )}
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Button variant="outlined" fullWidth onClick={() => setRegStep(0)} disabled={loading}>
                Back
              </Button>
              <Button
                variant="contained"
                fullWidth
                disabled={loading || !termsAccepted || !membershipTrialConsent}
                onClick={() => submitRegistration()}
                sx={{
                  py: 1.5,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : step2Cta}
              </Button>
            </Box>

            <Box sx={{ textAlign: 'center', pb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Already registered?{' '}
                <Link
                  to="/locsydashboard/login"
                  style={{ textDecoration: 'none', color: '#667eea', fontWeight: 600 }}
                >
                  Login here
                </Link>
              </Typography>
            </Box>
          </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default FranchiseeAdminRegister;















